import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarDays, CheckCircle2, Shield, ShieldCheck, Smartphone, SunMedium, Thermometer, Waves } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { TintVehiclePreview } from "@/components/landing/TintVehiclePreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://file-access-23.preview.emergentagent.com";
const API = `${BACKEND_URL}/api`;

const windowOptions = [
  {
    key: "full_vehicle_surround",
    label: "Full Vehicle Surround",
    description: "All side windows, rear glass, and roof glass previewed together.",
    tag: "Complete coverage",
  },
  {
    key: "front_two_windows",
    label: "Front 2 Windows",
    description: "Perfect if you only want the front side glass matched or upgraded.",
    tag: "Front side glass",
  },
  {
    key: "windshield",
    label: "Windshield",
    description: "Clear heat-rejection coverage across the main windshield area.",
    tag: "Full windshield",
  },
  {
    key: "sunstrip",
    label: "Windshield Sunstrip",
    description: "A shaded upper strip to cut harsh glare without covering the full shield.",
    tag: "Top strip only",
  },
  {
    key: "remove_old_tint",
    label: "Removal of Old Tint",
    description: "Add safe removal before the new film is installed.",
    tag: "Optional add-on",
  },
];

const packageOptions = [
  {
    key: "Standard Tint",
    tier: "Value +",
    title: "Standard Tint",
    background: "from-[#231208] via-[#734116] to-[#d48a2b]",
    features: [
      { icon: Thermometer, label: "Some Heat Rejection" },
      { icon: SunMedium, label: "UV Protection" },
    ],
  },
  {
    key: "Ceramic Tint",
    tier: "Better ++",
    title: "Ceramic Tint",
    background: "from-[#656566] via-[#8a8a8b] to-[#a5a5a6]",
    features: [
      { icon: Thermometer, label: "Strong Heat Rejection" },
      { icon: SunMedium, label: "99%+ UV Protection" },
      { icon: Smartphone, label: "Eliminates GPS & Cellular Interference" },
    ],
  },
  {
    key: "IR-Rejecting Ceramic Tint",
    tier: "Best Performer +++",
    title: "IR-Rejecting Ceramic Tint",
    background: "from-[#8e620a] via-[#b68316] to-[#d9aa34]",
    features: [
      { icon: Thermometer, label: "Maximum Heat Rejection" },
      { icon: SunMedium, label: "99%+ UV Protection" },
      { icon: Waves, label: "Infrared Rejection" },
      { icon: ShieldCheck, label: "Premium Cabin Comfort" },
    ],
  },
];

const initialTintForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  ideal_date: "",
  year: "",
  make: "",
  model: "",
  message: "",
  tint_type: "",
  quote_windows: [],
};

export const TintQuoteSection = () => {
  const [formData, setFormData] = useState(initialTintForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formPanelRef = useRef(null);

  const selectedPreviewAreas = useMemo(
    () => formData.quote_windows.filter((item) => item !== "remove_old_tint"),
    [formData.quote_windows],
  );

  const toggleWindowOption = (optionKey) => {
    setFormData((current) => ({
      ...current,
      quote_windows: current.quote_windows.includes(optionKey)
        ? current.quote_windows.filter((item) => item !== optionKey)
        : [...current.quote_windows, optionKey],
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleTintChoice = (tintType) => {
    setFormData((current) => ({ ...current, tint_type: tintType }));
  };

  const handlePackageSelect = (packageKey) => {
    handleTintChoice(packageKey);
    window.requestAnimationFrame(() => {
      formPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handlePackageClear = () => {
    setFormData((current) => ({
      ...current,
      tint_type: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.tint_type) {
      toast.error("Please choose the tint type you want quoted.");
      return;
    }

    if (!formData.quote_windows.length) {
      toast.error("Please select at least one window area for the quote.");
      return;
    }

    setIsSubmitting(true);

    const vehicle = `${formData.year} ${formData.make} ${formData.model}`.trim();
    const payload = {
      name: `${formData.first_name} ${formData.last_name}`.trim(),
      email: formData.email,
      phone: formData.phone,
      vehicle,
      service_interest: "Window Tint",
      preferred_contact: "text",
      message: [
        formData.message,
        `Tint type: ${formData.tint_type}`,
        `Quoted windows: ${formData.quote_windows.join(", ")}`,
        formData.ideal_date ? `Ideal date: ${formData.ideal_date}` : null,
      ].filter(Boolean).join("\n"),
      ...formData,
    };

    try {
      await axios.post(`${API}/inquiries`, payload);
      toast.success("Tint quote request received. Detail With D will follow up with your custom estimate.");
      setFormData(initialTintForm);
    } catch (error) {
      toast.error("Something went wrong while sending your tint quote. Please call or text the shop directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="border-t border-white/10 pb-24 pt-4 md:py-32" id="contact">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="grid gap-8 xl:grid-cols-[1.08fr_0.92fr]"
          initial={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          <div className="space-y-8" data-testid="tint-quote-left-column">
            <div className="space-y-4" data-testid="tint-quote-heading-group">
              <p className="section-kicker text-sm text-blue-400" data-testid="tint-quote-section-kicker">
                Window tint quote builder
              </p>
              <h2 className="text-4xl font-light leading-[0.95] text-white sm:text-5xl" data-testid="tint-quote-section-heading">
                Choose the glass you want tinted, pick your film, and send one focused quote request.
              </h2>
              <p className="max-w-2xl text-base leading-8 text-zinc-300" data-testid="tint-quote-section-description">
                This replaces the generic contact form with a tint-specific flow so your quote request reaches the team with the exact window areas, tint type, and vehicle details already mapped out.
              </p>
            </div>

            <div className="panel-surface luxury-outline overflow-hidden p-5 sm:p-6" data-testid="tint-roadmap-panel">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="section-kicker text-xs text-zinc-300" data-testid="tint-roadmap-kicker">
                    Tinter roadmap
                  </p>
                  <h3 className="mt-3 text-3xl font-medium leading-[1.02] text-white sm:text-[2.2rem]" data-testid="tint-roadmap-heading">
                    Which windows do you want tinted?
                  </h3>
                </div>

                <div className="glass-chip flex items-center gap-3 px-4 py-3 text-sm text-zinc-200" data-testid="tint-roadmap-summary-chip">
                  <CheckCircle2 className="h-4 w-4 text-blue-400" />
                  <span>{formData.quote_windows.length || 0} selections ready</span>
                </div>
              </div>

              <div className="mt-6 rounded-[2rem] border border-white/10 bg-[#22252b] p-5 sm:p-6" data-testid="live-window-preview-card">
                <TintVehiclePreview
                  activeAreas={selectedPreviewAreas}
                  className="mx-auto w-full max-w-[620px]"
                />

                <div className="mt-5 flex flex-wrap gap-2" data-testid="live-window-selection-tags">
                  {formData.quote_windows.length ? formData.quote_windows.map((item) => (
                    <div key={item} className="glass-chip px-3 py-2 text-xs tracking-[0.18em] text-zinc-200" data-testid={`selected-window-tag-${item}`}>
                      {windowOptions.find((option) => option.key === item)?.label ?? item}
                    </div>
                  )) : (
                    <p className="text-sm text-zinc-400" data-testid="selected-window-placeholder">Select window areas below to see them highlighted on the vehicle.</p>
                  )}
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="window-option-card-grid">
                {windowOptions.map((option) => {
                  const isActive = formData.quote_windows.includes(option.key);

                  return (
                    <button
                      key={option.key}
                      className={[
                        "panel-surface luxury-outline text-left p-4 transition-transform duration-300 hover:-translate-y-1",
                        isActive ? "ring-1 ring-blue-500" : "",
                      ].join(" ")}
                      data-testid={`window-option-card-${option.key}`}
                      onClick={() => toggleWindowOption(option.key)}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="section-kicker text-xs text-blue-400" data-testid={`window-option-tag-${option.key}`}>
                            {option.tag}
                          </p>
                          <p className="mt-2 text-xl font-medium leading-tight text-white" data-testid={`window-option-title-${option.key}`}>
                            {option.label}
                          </p>
                        </div>
                        <div className={isActive ? "glass-chip bg-blue-500/20 px-3 py-2 text-xs text-blue-200" : "glass-chip px-3 py-2 text-xs text-zinc-300"} data-testid={`window-option-state-${option.key}`}>
                          {isActive ? "Selected" : "Tap to add"}
                        </div>
                      </div>

                      <TintVehiclePreview
                        activeAreas={option.key === "remove_old_tint" ? [] : [option.key]}
                        className="mt-4 h-[128px] w-full"
                      />

                    <p className="mt-3 text-sm leading-7 text-zinc-200" data-testid={`window-option-description-${option.key}`}>
                        {option.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-6" data-testid="tint-package-section">
              <div className="space-y-4 text-center" data-testid="tint-package-heading-group">
                <p className="section-kicker text-sm text-zinc-300" data-testid="tint-package-kicker">
                  Tinters roadmap
                </p>
                <h3 className="text-4xl font-light leading-[0.95] text-white sm:text-5xl" data-testid="tint-package-heading">
                  We offer numerous levels of tint.
                </h3>
                <div className="mx-auto h-1 w-48 rounded-full bg-[#c71d3d]" data-testid="tint-package-heading-divider" />
              </div>

              <div className="grid gap-6" data-testid="tint-package-card-stack">
                {packageOptions.map((option) => {
                  const isSelected = formData.tint_type === option.key;
                  const slug = option.key.toLowerCase().replace(/[^a-z0-9]+/g, "-");

                  return (
                    <article
                      key={option.key}
                      className={[
                        "overflow-hidden rounded-[2rem] border border-white/15 bg-gradient-to-b p-0 shadow-[0_22px_60px_rgba(0,0,0,0.28)]",
                        option.background,
                        isSelected ? "ring-2 ring-white/70" : "",
                      ].join(" ")}
                      data-testid={`tint-package-card-${slug}`}
                    >
                      <div className="px-6 py-5 text-center" data-testid={`tint-package-tier-${slug}`}>
                        <p className="section-kicker text-sm text-white">{option.tier}</p>
                      </div>

                      <div className="bg-white px-4 py-3 text-center" data-testid={`tint-package-title-strip-${slug}`}>
                        <p className="text-[1.7rem] font-medium uppercase tracking-[0.28em] text-black sm:text-[2rem]">
                          {option.title}
                        </p>
                      </div>

                      <div className="space-y-0 px-6 py-7 text-center text-white sm:px-8 sm:py-8" data-testid={`tint-package-features-${slug}`}>
                        {option.features.map((feature, index) => {
                          const Icon = feature.icon;

                          return (
                            <div key={feature.label} className="py-7" data-testid={`tint-package-feature-${slug}-${index}`}>
                              <Icon className="mx-auto h-12 w-12 text-white" data-testid={`tint-package-feature-icon-${slug}-${index}`} />
                              <p className="mt-5 text-xl uppercase tracking-[0.3em] text-white sm:text-[1.7rem]" data-testid={`tint-package-feature-label-${slug}-${index}`}>
                                {feature.label}
                              </p>
                              {index !== option.features.length - 1 ? <div className="mt-7 h-px w-full bg-white/70" /> : null}
                            </div>
                          );
                        })}

                        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid={`tint-package-action-row-${slug}`}>
                          <button
                            className="inline-flex w-full items-center justify-center rounded-[1.35rem] bg-white px-5 py-5 text-xl font-medium text-black shadow-[0_14px_30px_rgba(0,0,0,0.2)] transition-transform duration-300 hover:-translate-y-1"
                            data-testid={`tint-package-select-button-${slug}`}
                            onClick={() => handlePackageSelect(option.key)}
                            type="button"
                          >
                            {isSelected ? "Tint Selected" : "Select Tint"}
                          </button>
                          <button
                            className="inline-flex w-full items-center justify-center rounded-[1.35rem] border border-white/55 bg-transparent px-5 py-5 text-xl font-medium text-white transition-transform duration-300 hover:-translate-y-1 hover:bg-white/10"
                            data-testid={`tint-package-clear-button-${slug}`}
                            onClick={handlePackageClear}
                            type="button"
                          >
                            Remove Tint
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>

          <motion.div className="panel-surface luxury-outline p-6 sm:p-8" data-testid="tint-quote-form-panel" ref={formPanelRef} whileHover={{ y: -4 }}>
            <div className="flex items-center gap-3 text-white" data-testid="tint-quote-form-header">
              <CalendarDays className="h-6 w-6 text-blue-400" />
              <div>
                <p className="section-kicker text-xs text-zinc-300" data-testid="tint-quote-form-kicker">Get a quote today</p>
                <p className="mt-2 text-[1.7rem] font-medium leading-[1.02] sm:text-[1.95rem]" data-testid="tint-quote-form-title">Window tint quote form</p>
              </div>
            </div>

            <form className="mt-8 grid gap-5" data-testid="tint-quote-form" onSubmit={handleSubmit}>
              <div data-testid="tint-package-summary-field">
                <label className="mb-3 block text-sm font-semibold text-white" htmlFor="tint-package-summary">Tint Package*</label>
                <div className="rounded-[1.5rem] border border-white/10 bg-white/5 px-4 py-4" data-testid="tint-package-summary" id="tint-package-summary">
                  {formData.tint_type ? (
                    <div className="glass-chip inline-flex px-3 py-2 text-sm text-zinc-100" data-testid="tint-package-summary-value">{formData.tint_type}</div>
                  ) : (
                      <span className="text-sm text-zinc-200" data-testid="tint-package-summary-placeholder">Choose a tint package from the cards on the left.</span>
                  )}
                </div>
              </div>

              <div data-testid="tint-quote-selected-windows-field">
                <label className="mb-3 block text-sm font-semibold text-white" htmlFor="tint-selected-summary">Quote These Windows:*</label>
                <div className="rounded-[1.5rem] border border-white/10 bg-white/5 px-4 py-4" data-testid="tint-selected-summary" id="tint-selected-summary">
                  <div className="flex flex-wrap gap-2" data-testid="tint-selected-summary-tags">
                    {formData.quote_windows.length ? formData.quote_windows.map((item) => (
                      <div key={item} className="glass-chip px-3 py-2 text-sm text-zinc-200" data-testid={`tint-selected-summary-tag-${item}`}>
                        {windowOptions.find((option) => option.key === item)?.label ?? item}
                      </div>
                    )) : (
                      <span className="text-sm text-zinc-200" data-testid="tint-selected-summary-placeholder">Choose one or more window options from the left.</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div data-testid="tint-form-first-name-field">
                  <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-first-name">First name*</label>
                  <Input className="h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-first-name-input" id="tint-first-name" minLength={2} name="first_name" onChange={handleChange} placeholder="First name" required value={formData.first_name} />
                </div>
                <div data-testid="tint-form-last-name-field">
                  <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-last-name">Last name*</label>
                  <Input className="h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-last-name-input" id="tint-last-name" minLength={2} name="last_name" onChange={handleChange} placeholder="Last name" required value={formData.last_name} />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div data-testid="tint-form-email-field">
                  <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-email">Email*</label>
                  <Input className="h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-email-input" id="tint-email" name="email" onChange={handleChange} placeholder="Email" required type="email" value={formData.email} />
                </div>
                <div data-testid="tint-form-phone-field">
                  <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-phone">Phone*</label>
                  <Input className="h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-phone-input" id="tint-phone" minLength={10} name="phone" onChange={handleChange} placeholder="Phone" required value={formData.phone} />
                </div>
              </div>

              <div data-testid="tint-form-ideal-date-field">
                <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-ideal-date">Ideal date?</label>
                <Input className="h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-ideal-date-input" id="tint-ideal-date" name="ideal_date" onChange={handleChange} placeholder="Preferred day or time frame" value={formData.ideal_date} />
              </div>

              <div className="space-y-4" data-testid="tint-form-vehicle-details-section">
                <div>
                  <p className="text-2xl font-medium text-white" data-testid="tint-form-vehicle-details-heading">Vehicle Details</p>
                  <p className="mt-2 text-sm leading-7 text-zinc-300" data-testid="tint-form-vehicle-details-description">
                    Please provide details of your vehicle for the most accurate quote.
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div data-testid="tint-form-year-field">
                    <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-year">Year*</label>
                    <Input className="h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-year-input" id="tint-year" name="year" onChange={handleChange} placeholder="2025" required value={formData.year} />
                  </div>
                  <div data-testid="tint-form-make-field">
                    <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-make">Make*</label>
                    <Input className="h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-make-input" id="tint-make" name="make" onChange={handleChange} placeholder="Audi" required value={formData.make} />
                  </div>
                  <div data-testid="tint-form-model-field">
                    <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-model">Model*</label>
                    <Input className="h-12 rounded-2xl border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-model-input" id="tint-model" name="model" onChange={handleChange} placeholder="A4" required value={formData.model} />
                  </div>
                </div>
              </div>

              <div data-testid="tint-form-message-field">
                <label className="mb-2 block text-sm font-semibold text-white" htmlFor="tint-message">Message To The Team:</label>
                <Textarea className="min-h-[150px] rounded-[1.5rem] border-white/10 bg-white/5 text-white placeholder:text-zinc-300" data-testid="tint-form-message-input" id="tint-message" minLength={10} name="message" onChange={handleChange} placeholder="Message or special requests for the team" required value={formData.message} />
              </div>

              <div className="rounded-[1.5rem] border border-dashed border-white/15 bg-black/20 px-4 py-4 text-sm text-zinc-200" data-testid="tint-form-recaptcha-placeholder">
                reCAPTCHA can be added later if needed. For now, this form is ready to collect tint quote requests.
              </div>

              <Button className="glass-button glass-button-primary h-14 text-sm tracking-[0.18em] text-white transition-colors duration-300 hover:bg-blue-500" data-testid="tint-form-submit-button" disabled={isSubmitting} type="submit">
                {isSubmitting ? "Sending tint quote..." : "Get My Tint Quote"}
              </Button>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};